/**
 * `/poste/colis` — what the desk is holding, and handing over.
 *
 * A list with two actions, on the same reasoning as `/poste/arrivees`: the
 * gardien is at this screen for eight hours, so a parcel arriving or leaving
 * must never take the screen away from the next person at the window.
 *
 * The station's dark surface is inherited from the `/poste` layout; the
 * card/border classes here mirror `arrivees.tsx` rather than reaching for
 * `Card`, which is a light-canvas component.
 */
import type { RouterOutputs } from "@gately/api";
import { useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";

import { cn } from "@gately/ui";
import { Button } from "@gately/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@gately/ui/dialog";
import { Input } from "@gately/ui/input";
import { Label } from "@gately/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@gately/ui/select";
import { toast } from "@gately/ui/toast";
import {
  PARCEL_CARRIER_MAX,
  PARCEL_DESCRIPTION_MAX,
  PARCEL_HANDED_TO_LABEL_MAX,
  unitLabel,
} from "@gately/validators";

import { StationChrome } from "~/components/station/station-chrome";
import { StationEyebrow } from "~/components/station/station-screen";
import { orpc } from "~/lib/orpc";
import { isConflictError, isNotFoundError } from "~/lib/orpc-error";
import { clockTime } from "~/lib/station/scan-format";
import { useOrgTimeZone } from "~/lib/use-me";
import { useTerms } from "~/lib/use-terms";

export const Route = createFileRoute("/poste/colis")({
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(orpc.shift.current.queryOptions()),
      context.queryClient.ensureQueryData(
        orpc.parcel.listAtDesk.queryOptions(),
      ),
      context.queryClient.ensureQueryData(orpc.me.get.queryOptions()),
    ]);
  },
  component: StationParcelsPage,
});

type DeskParcel = RouterOutputs["parcel"]["listAtDesk"]["waiting"][number];

function StationParcelsPage() {
  const terms = useTerms();
  const timeZone = useOrgTimeZone();
  const [depositOpen, setDepositOpen] = useState(false);
  const [handOverFor, setHandOverFor] = useState<DeskParcel | null>(null);

  const { data } = useSuspenseQuery(orpc.parcel.listAtDesk.queryOptions());

  return (
    <StationChrome>
      <div className="mx-auto w-full max-w-3xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">
              {terms.parcelsAtDeskTitle}
            </h1>
            <StationEyebrow className="mt-1.5">
              {data.waiting.length === 0
                ? "Aucun colis en attente"
                : `${data.waiting.length} en attente`}
            </StationEyebrow>
          </div>
          <Button className="h-12" onClick={() => setDepositOpen(true)}>
            <Plus className="size-4" />
            Déposer un colis
          </Button>
        </div>

        <div className="mt-6 space-y-3">
          {data.waiting.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-8 text-center">
              <p className="text-base font-medium">Rien {terms.atTheDesk}.</p>
              <p className="text-muted-foreground mt-2 text-[0.9375rem] leading-relaxed">
                Pour un colis laissé par un livreur, ouvrez « Déposer un colis »
                : le foyer est prévenu immédiatement.
              </p>
            </div>
          ) : (
            data.waiting.map((p) => (
              // The whole card is the hand-over button: the gardien has one
              // hand on a parcel, so the target is the row, not a chevron.
              <button
                key={p.id}
                type="button"
                onClick={() => setHandOverFor(p)}
                className={cn(
                  "w-full rounded-xl border p-4 text-left transition-colors",
                  // Amber from three days — the same threshold the reminder
                  // fires on, so the screen and the notification agree.
                  p.stale
                    ? "border-warning/40 bg-warning/10"
                    : "border-white/12 bg-white/[0.04] hover:bg-white/8",
                )}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-lg font-semibold">
                      {p.unitLabel}
                    </p>
                    <p className="text-muted-foreground mt-0.5 text-[0.9375rem]">
                      {[p.carrier, p.description].filter(Boolean).join(" · ") ||
                        "—"}
                    </p>
                  </div>
                  <span className="text-muted-foreground shrink-0 font-mono text-sm">
                    {p.sinceLabel}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>

        {data.handedToday.length > 0 ? (
          <div className="mt-8">
            <StationEyebrow>
              Remis aujourd&apos;hui ({data.handedToday.length})
            </StationEyebrow>
            <ul className="mt-3 space-y-2">
              {data.handedToday.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-white/10 px-4 py-2.5"
                >
                  <span className="truncate text-[0.9375rem]">
                    {p.unitLabel}
                    {p.handedToName ? ` · ${p.handedToName}` : ""}
                  </span>
                  <span className="text-muted-foreground shrink-0 font-mono text-sm">
                    {p.handedAt ? clockTime(p.handedAt, timeZone) : "—"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <DepositDialog open={depositOpen} onOpenChange={setDepositOpen} />
      <HandOverDialog
        // Keyed per parcel so the picked collector never leaks into the next.
        key={handOverFor?.id ?? "none"}
        parcel={handOverFor}
        onClose={() => setHandOverFor(null)}
      />
    </StationChrome>
  );
}

function DepositDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const terms = useTerms();
  const queryClient = useQueryClient();
  const [unitId, setUnitId] = useState("");
  const [carrier, setCarrier] = useState("");
  const [description, setDescription] = useState("");

  // Loaded only while the dialog is open — the same `enabled` trick
  // arrivees.tsx's CreateDialog uses for its unit select.
  const units = useQuery({
    ...orpc.unit.list.queryOptions({ input: { limit: 500 } }),
    enabled: open,
  });

  // Every exit clears the form — success, « Annuler », Esc, the backdrop.
  // Not just the successful one: a gardien who abandoned a deposit and opened
  // the dialog again would otherwise find the previous unit still chosen, and
  // « Enregistrer » one parcel onto the wrong household.
  const close = () => {
    onOpenChange(false);
    setUnitId("");
    setCarrier("");
    setDescription("");
  };

  const deposit = useMutation(
    orpc.parcel.deposit.mutationOptions({
      onSuccess: async () => {
        close();
        toast.success("Colis enregistré.");
        await queryClient.invalidateQueries({
          queryKey: orpc.parcel.listAtDesk.key(),
        });
      },
      onError: (e) => toast.error(e.message || "Action impossible."),
    }),
  );

  return (
    <Dialog
      open={open}
      // A dismissal mid-write would leave the gardien unsure whether the
      // parcel was recorded; the write is short, so the dialog simply holds.
      onOpenChange={(o) => {
        if (deposit.isPending) return;
        if (o) onOpenChange(true);
        else close();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Déposer un colis</DialogTitle>
          <DialogDescription>
            Le foyer est prévenu immédiatement qu&apos;un colis l&apos;attend{" "}
            {terms.atTheDesk}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="parcel-unit">{terms.unitRowLabel}</Label>
            <Select value={unitId} onValueChange={setUnitId}>
              <SelectTrigger id="parcel-unit" className="h-12 w-full">
                <SelectValue
                  placeholder={units.isPending ? "Chargement…" : "Choisir…"}
                />
              </SelectTrigger>
              <SelectContent>
                {(units.data ?? []).map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {unitLabel(u)}
                    {u.ownerName ? ` · ${u.ownerName}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="parcel-carrier">Transporteur (optionnel)</Label>
            <Input
              id="parcel-carrier"
              className="h-12"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              placeholder="ex. Amana"
              maxLength={PARCEL_CARRIER_MAX}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="parcel-description">Description (optionnel)</Label>
            <Input
              id="parcel-description"
              className="h-12"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="ex. 2 cartons"
              maxLength={PARCEL_DESCRIPTION_MAX}
            />
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            disabled={deposit.isPending}
            onClick={close}
          >
            Annuler
          </Button>
          <Button
            disabled={!unitId || deposit.isPending}
            onClick={() =>
              deposit.mutate({
                unitId,
                // Blank optionals are omitted, never sent as "": the server's
                // schema rejects an empty string.
                carrier: carrier.trim() || undefined,
                description: description.trim() || undefined,
              })
            }
          >
            {deposit.isPending ? "Enregistrement…" : "Enregistrer le colis"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const OTHER = "__other__";

/**
 * Who took the parcel — a roster name or a typed one, never both.
 *
 * The roster comes back EMPTY for a parcel left for a foyer that has since
 * moved out, and « Autre personne » is then the only answer there is; the
 * server refuses a user id for such a parcel, so the picker must not offer
 * one.
 */
function HandOverDialog({
  parcel,
  onClose,
}: {
  parcel: DeskParcel | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<string | null>(null);
  const [label, setLabel] = useState("");

  const household = useQuery({
    ...orpc.parcel.householdForHandOver.queryOptions({
      input: { parcelId: parcel?.id ?? "" },
    }),
    enabled: parcel !== null,
  });

  const handOver = useMutation(
    orpc.parcel.handOver.mutationOptions({
      onSuccess: async () => {
        onClose();
        toast.success("Colis remis.");
        await queryClient.invalidateQueries({
          queryKey: orpc.parcel.listAtDesk.key(),
        });
      },
      onError: async (e) => {
        // A verdict on the server's own state: CONFLICT means another desk
        // recorded the hand-over first, NOT_FOUND that the row is gone.
        // Neither can be retried, so the dialog closes and the refreshed list
        // is the explanation.
        if (isConflictError(e) || isNotFoundError(e)) {
          onClose();
          toast.error(e.message || "Ce colis a déjà été remis.");
          await queryClient.invalidateQueries({
            queryKey: orpc.parcel.listAtDesk.key(),
          });
          return;
        }
        // Anything else — a dropped request, a 500 — says nothing about the
        // parcel. The dialog and the typed name stay put so the gardien can
        // press again rather than retype.
        toast.error(e.message || "Action impossible.");
      },
    }),
  );

  const isOther = picked === OTHER;
  // Nothing may be confirmed before the roster has settled: until then it is
  // unknown whether this parcel HAS a foyer to offer.
  const canConfirm =
    household.isSuccess &&
    (isOther ? label.trim().length > 0 : picked !== null);

  return (
    <Dialog
      open={parcel !== null}
      onOpenChange={(o) => {
        if (!o && !handOver.isPending) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Qui récupère ce colis ?</DialogTitle>
          <DialogDescription>
            {parcel ? `${parcel.unitLabel} · ${parcel.sinceLabel}` : ""}
          </DialogDescription>
        </DialogHeader>

        {/* « Autre personne » is only offered once the roster has SETTLED:
            an empty list is the straddling-parcel signal, and rendering it
            while the query is in flight — or failed — would tell the gardien
            this parcel has no foyer when nobody has looked yet. */}
        {household.isPending ? (
          <p className="text-muted-foreground text-[0.9375rem]">Chargement…</p>
        ) : household.isError ? (
          <div className="space-y-3">
            <p className="text-destructive text-[0.9375rem]">
              Impossible de charger le foyer.
            </p>
            <Button
              variant="outline"
              disabled={household.isFetching}
              onClick={() => void household.refetch()}
            >
              {household.isFetching ? "Nouvelle tentative…" : "Réessayer"}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {household.data.map((p) => (
              <button
                key={p.userId}
                type="button"
                aria-pressed={picked === p.userId}
                disabled={handOver.isPending}
                onClick={() => setPicked(p.userId)}
                className={cn(
                  "w-full rounded-lg border px-4 py-3 text-left text-[0.9375rem] font-medium transition-colors",
                  picked === p.userId
                    ? "border-primary bg-primary/10"
                    : "border-border hover:bg-accent",
                )}
              >
                {p.displayName}
              </button>
            ))}
            <button
              type="button"
              aria-pressed={isOther}
              disabled={handOver.isPending}
              onClick={() => setPicked(OTHER)}
              className={cn(
                "w-full rounded-lg border px-4 py-3 text-left text-[0.9375rem] font-medium transition-colors",
                isOther
                  ? "border-primary bg-primary/10"
                  : "border-border hover:bg-accent",
              )}
            >
              Autre personne
            </button>
            {isOther ? (
              <div className="space-y-1.5 pt-1">
                <Label htmlFor="handed-to-label">Nom de la personne</Label>
                <Input
                  id="handed-to-label"
                  className="h-12"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="ex. la femme de ménage"
                  maxLength={PARCEL_HANDED_TO_LABEL_MAX}
                  autoFocus
                />
              </div>
            ) : null}
          </div>
        )}

        <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            disabled={handOver.isPending}
            onClick={onClose}
          >
            Annuler
          </Button>
          <Button
            disabled={!canConfirm || handOver.isPending}
            onClick={() => {
              if (!parcel) return;
              // Exactly one collector, which is the server's rule too: a
              // typed name OR a roster id, spread so the other key is absent
              // rather than undefined-and-present.
              handOver.mutate({
                parcelId: parcel.id,
                ...(isOther
                  ? { handedToLabel: label.trim() }
                  : { handedToUserId: picked ?? undefined }),
              });
            }}
          >
            {handOver.isPending ? "Enregistrement…" : "Marquer remis"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
